import React, { useState } from 'react';
import { CheckCircle2, CornerUpLeft, X } from 'lucide-react';
import apiService from '../../services/apiService';

/**
 * B2 — bulk review action bar. Appears above the status board when one or more
 * rows are selected. Approve/return apply a single shared comment across the
 * selection; the server runs the same per-package permission + quality-gate +
 * red-constraint guard as single-row review, so partial success is expected and
 * reported per id. Return requires a shared comment.
 */
const ForecastBulkActionBar = ({ cycleCode, actor, selectedIds, onClear, onDone }) => {
  const [action, setAction] = useState(null); // 'approve' | 'return' | null
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const count = selectedIds.length;
  if (count === 0 && !result) return null;

  const run = async (chosen) => {
    if (chosen === 'return' && !comment.trim()) {
      setError('A shared comment is required to return submissions.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await apiService.bulkTransitionForecastSubmissions({
        cycleCode,
        actor,
        action: chosen,
        comment: comment.trim() || undefined,
        submissionIds: selectedIds,
        applyTo: 'selected'
      });
      setResult(res);
      setAction(null);
      setComment('');
      if (onDone) onDone(res);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Bulk action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="forecast-panel forecast-bulk-bar">
      <div className="forecast-bulk-bar-head">
        <strong>{count} selected</strong>
        <button type="button" className="forecast-home-action ghost" onClick={onClear}>
          <X size={12} /> Clear
        </button>
        <div className="forecast-bulk-bar-actions">
          <button
            type="button"
            className={`forecast-home-action ${action === 'approve' ? 'active' : ''}`}
            disabled={busy || count === 0}
            onClick={() => setAction(action === 'approve' ? null : 'approve')}
          >
            <CheckCircle2 size={13} /> Approve selected
          </button>
          <button
            type="button"
            className={`forecast-home-action ${action === 'return' ? 'active' : ''}`}
            disabled={busy || count === 0}
            onClick={() => setAction(action === 'return' ? null : 'return')}
          >
            <CornerUpLeft size={13} /> Return selected
          </button>
        </div>
      </div>

      {action && (
        <div className="forecast-bulk-bar-confirm">
          <textarea
            rows={2}
            placeholder={action === 'return'
              ? 'Shared comment (required to return the whole selection)…'
              : 'Optional shared approval note (required if any package has red constraints)…'}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <button
            type="button"
            className="forecast-primary-button compact"
            disabled={busy || (action === 'return' && !comment.trim())}
            onClick={() => run(action)}
          >
            {busy ? 'Working…' : `Confirm ${action} of ${count}`}
          </button>
        </div>
      )}

      {error && <p className="forecast-submit-note" style={{ color: 'var(--ace-danger, #e5241d)' }}>{error}</p>}

      {result && (
        <div className="forecast-bulk-bar-result">
          <span className="forecast-health-chip moss">{result.succeeded?.length || 0} succeeded</span>
          {(result.failed?.length || 0) > 0 && (
            <span className="forecast-health-chip red">{result.failed.length} failed</span>
          )}
          {(result.failed || []).map((f) => (
            <div key={f.submissionId} className="forecast-bulk-bar-fail">
              <span className="forecast-mono">{String(f.submissionId).slice(0, 8)}</span> — {f.error}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default ForecastBulkActionBar;
